import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useStandardAlertStore } from '../store/portal/alertStore';
import { getAlertMessage } from '../constants/alertMessages';
import { usePageOwnerIdentityStore } from '../store/pageOwnerIdentityStore';
import { convertAuiToHandle, convertHandleToAui } from '../api/authAPI';
import { AlertPosition, AlertType } from '../enum/EnumRepository';
import { useValidation } from './useValidation';
import { useSettingStore } from '../store/settingStore';
import { useProjectListStore } from '../store/projectListStore';
import { useContactStore } from '../store/contactStore';
import { useContactPropertyVisibleStore } from '../store/contactPropertyVisibleStore';
import { useMemberInfoStore } from '../store/memberInfoStore';
import { useCareerListStore } from '../store/careerStore';
import { useGroupMemberStore } from '../store/groupMemberStore';
import { useGroupMemberTagStore } from '../store/groupMemberTagStore';
import { useTextListStore } from '../store/TextListStore';
import { useWorkListStore } from '../store/WorkListStore';
import { useWorkTagStore } from '../store/workTagStore';
import { useTextTagStore } from '../store/textTagStore';
import { useProjectStore } from '../store/projectStore';
import { useProjectInfoListStore } from '../store/projectInfoStore';
import { useProjectElementListStore } from '../store/projectElementStore';
import { useAboutPageSettingStore } from '../store/aboutPageSettingStore';

/**
 * [url path resolver]
 * 
 * URL 경로의 사용자 식별자(AUI 또는 핸들)를 검증, 변환하여  
 * 페이지 소유자 정보를 저장
 * 
 * 같은 pageOwner가 이미 세팅되어 있으면 리턴
 */
export const useUserPathResolver = (raw: string | undefined) => {
  const navigate = useNavigate();
  const location = useLocation();
  const clearPageOwner = usePageOwnerIdentityStore((s) => s.clearPageOwner);
  const setPageOwner = usePageOwnerIdentityStore((s) => s.setPageOwner);
  const clearSetting = useSettingStore((s) => s.clearSetting);
  const clearProjectList = useProjectListStore((s) => s.clearProjectList);
  const clearProject = useProjectStore((s) => s.clearProject);
  const clearProjectInfoList = useProjectInfoListStore((s) => s.clearProjectInfoList);
  const clearProjectElementList = useProjectElementListStore((s) => s.clearProjectElementList);
  const clearContact = useContactStore((s) => s.clearContact);
  const clearContactPropertyVisible = useContactPropertyVisibleStore(
    (s) => s.clearContactPropertyVisible,
  );
  const clearMemberInfo = useMemberInfoStore((s) => s.clearMemberInfo);
  const clearCareers = useCareerListStore((s) => s.clearCareers);
  const clearAboutPageSetting = useAboutPageSettingStore(
    (s) => s.clearAboutPageSetting,
  );
  const clearGroupMembers = useGroupMemberStore((s) => s.clearGroupMembers);
  const clearGroupMemberTags = useGroupMemberTagStore((s) => s.clearGroupMemberTags);
  const clearTextList = useTextListStore((s) => s.clearTextList);
  const clearTextTags = useTextTagStore((s) => s.clearTags);
  const clearWorkList = useWorkListStore((s) => s.clearWorkList);
  const clearWorkTags = useWorkTagStore((s) => s.clearTags);
  const { enqueueAlert } = useStandardAlertStore();
  const { isValidHandle, isValidAui } = useValidation();

  useEffect(() => {
    let cancelled = false;

    const resolveUserPath = async () => {
      if (!raw) {
        clearPageOwner();
        clearSetting();
        clearProjectList();
        clearProject();
        clearProjectInfoList();
        clearProjectElementList();
        clearContact();
        clearContactPropertyVisible();
        clearMemberInfo();
        clearCareers();
        clearAboutPageSetting();
        clearGroupMembers();
        clearGroupMemberTags();
        clearTextList();
        clearTextTags();
        clearWorkList();
        clearWorkTags();
        return;
      }

      const currentPageOwner = usePageOwnerIdentityStore.getState().pageOwner;
      if (currentPageOwner && `@${currentPageOwner.handle}` === raw) {
        return;
      }

      const navigateToHandle = (handle: string) => {
        const newPath = location.pathname.replace(`/${raw}`, `/@${handle}`);
        if (location.pathname === newPath) return;

        navigate(
          {
            pathname: newPath,
            search: location.search,
            hash: location.hash,
          },
          { replace: true },
        );
      };

      const showUserNotFound = () => {
        enqueueAlert({
          type: AlertType.ALERT,
          position: AlertPosition.TOP,
          content: getAlertMessage('USER_NOT_FOUND'),
          callBack: () => {
            navigate("/", { replace: true });
          },
        });
      };

      clearPageOwner();
      clearSetting();
      clearProjectList();
      clearProject();
      clearProjectInfoList();
      clearProjectElementList();
      clearContact();
      clearContactPropertyVisible();
      clearMemberInfo();
      clearCareers();
      clearAboutPageSetting();
      clearGroupMembers();
      clearGroupMemberTags();
      clearTextList();
      clearTextTags();
      clearWorkList();
      clearWorkTags();

      const isAui = raw.startsWith('@') ? false : true;
      if (isAui) {
        const aui = raw;
        if (!isValidAui(aui)) {
          navigate("/error", { replace: true });
          return;
        }
        try {
          const { data } = await convertAuiToHandle(aui);
          if (cancelled) return;

          const resolvedHandle = data.value;
          if (resolvedHandle) {
            navigateToHandle(resolvedHandle);
          } else {
            showUserNotFound();
          }
        } catch (e) {
          if (cancelled) return;
          showUserNotFound();
        }
      } else {
        const handle = raw.slice(1);
        if (!isValidHandle(handle)) {
          navigate("/error", { replace: true });
          return;
        }
        try {
          const { data } = await convertHandleToAui(handle);
          if (cancelled) return;

          const resolvedAui = data.value;
          if (resolvedAui) {
            setPageOwner(resolvedAui, handle);
          } else {
            showUserNotFound();
          }
        } catch (e) {
          if (cancelled) return;
          showUserNotFound();
        }
      }
    }
    void resolveUserPath();

    return () => {
      cancelled = true;
    };
  }, [raw, location.pathname, location.search, location.hash]);
};
